import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { blendingBatchesApi, packagingBatchesApi, productsApi, suppliesApi, warehousesApi } from '../../../services/api'
import StatusBadge from './StatusBadge'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

interface Warehouse {
  id: number
  name: string
  is_active: boolean
}

interface Supply {
  id: number
  name: string
  current_cost_per_kg: number
}

interface Product {
  id: number
  name: string
  is_active: boolean
  producto_maestro: string
}

interface BlendingBatchOption {
  id: number
  batch_code: string
  kg_remaining: number | null
  status: string
}

interface PackagingBatch {
  id: number
  batch_code: string
  source_blending_batch_id: number
  source_batch_code: string
  kg_input: number
  product_cost_allocated: number
  label_cost_per_kg: number
  bag_cost_per_kg: number
  labeling_cost: number
  packaging_cost: number
  total_cost: number | null
  unit_cost_per_kg: number | null
  kg_remaining: number | null
  product_id: number
  product_name: string
  status: string
  warehouse_id: number
  warehouse_name: string
  requested_date: string | null
  completed_date: string | null
  notes: string | null
  is_active: boolean
}

interface CreateForm {
  source_blending_batch_id: string
  kg_input: string
  product_id: string
  label_cost_per_kg: string
  bag_cost_per_kg: string
  warehouse_id: string
  notes: string
}

const emptyCreateForm: CreateForm = {
  source_blending_batch_id: '',
  kg_input: '',
  product_id: '',
  label_cost_per_kg: '',
  bag_cost_per_kg: '',
  warehouse_id: '',
  notes: '',
}

interface EditForm {
  warehouse_id: string
  notes: string
  origin_warehouse_id: string
  transit_eta: string
  status: 'at_rest' | 'in_transit'
  product_id: string
  kg_input: string
}

function findSupplyCost(supplies: Supply[], name: string): string {
  const s = supplies.find((sup) => sup.name.toLowerCase() === name.toLowerCase())
  return s ? String(s.current_cost_per_kg) : ''
}

export default function PackagingTab() {
  const [batches, setBatches] = useState<PackagingBatch[]>([])
  const [blendingBatches, setBlendingBatches] = useState<BlendingBatchOption[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [supplies, setSupplies] = useState<Supply[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<CreateForm>(emptyCreateForm)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [editingBatch, setEditingBatch] = useState<PackagingBatch | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({
    warehouse_id: '',
    notes: '',
    origin_warehouse_id: '',
    transit_eta: '',
    status: 'at_rest',
    product_id: '',
    kg_input: '',
  })
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [completingId, setCompletingId] = useState<number | null>(null)
  const [cancelingId, setCancelingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      setLoadError('')
      const [batchesRes, blendRes, whRes, suppliesRes, productsRes] = await Promise.all([
        packagingBatchesApi.list(),
        blendingBatchesApi.list(),
        warehousesApi.list(),
        suppliesApi.list(),
        productsApi.list(),
      ])
      setBatches(batchesRes.data.batches || [])
      setBlendingBatches(
        (blendRes.data.batches || []).filter(
          (b: BlendingBatchOption) => b.status === 'at_rest' && (b.kg_remaining || 0) > 0
        )
      )
      setWarehouses(whRes.data.warehouses || [])
      setSupplies(suppliesRes.data.supplies || [])
      setProducts(productsRes.data.products || [])
    } catch (err) {
      console.error('Error fetching packaging batches:', err)
      setLoadError('No se pudieron cargar los lotes de empaquetado. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeWarehouses = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses])
  const activeProducts = useMemo(() => products.filter((p) => p.is_active), [products])

  // Group products by producto_maestro so variants of the same underlying
  // product (e.g. different labeling/presentations sharing pooled stock)
  // appear together in the picker, sorted alphabetically by group.
  const productGroups = useMemo(() => {
    const groups = new Map<string, Product[]>()
    products.forEach((p) => {
      const key = p.producto_maestro || p.name
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(p)
    })
    return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0], 'es'))
  }, [products])

  const filteredBatches = useMemo(() => {
    if (!statusFilter) return batches
    return batches.filter((b) => b.status === statusFilter)
  }, [batches, statusFilter])

  const { sortedData: sortedBatches, sortConfig, requestSort } = useSortableData(filteredBatches)

  const selectedSource = useMemo(
    () => blendingBatches.find((b) => String(b.id) === form.source_blending_batch_id),
    [blendingBatches, form.source_blending_batch_id]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm({
      ...emptyCreateForm,
      label_cost_per_kg: findSupplyCost(supplies, 'Etiqueta'),
      bag_cost_per_kg: findSupplyCost(supplies, 'Bolsa'),
    })
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.source_blending_batch_id) {
      setError('Selecciona el lote de mezclado de origen')
      return
    }
    const kg = parseFloat(form.kg_input)
    if (!kg || kg <= 0) {
      setError('Ingresa un peso de entrada válido')
      return
    }
    if (selectedSource && kg > Number(selectedSource.kg_remaining)) {
      setError(`Solo quedan ${Number(selectedSource.kg_remaining).toFixed(2)} kg disponibles en este lote`)
      return
    }
    if (!form.product_id) {
      setError('Selecciona el producto que se va a empaquetar')
      return
    }
    if (!form.warehouse_id) {
      setError('Selecciona un almacén')
      return
    }
    setSaving(true)
    setError('')
    try {
      await packagingBatchesApi.create({
        source_blending_batch_id: Number(form.source_blending_batch_id),
        kg_input: kg,
        product_id: Number(form.product_id),
        label_cost_per_kg: form.label_cost_per_kg ? parseFloat(form.label_cost_per_kg) : undefined,
        bag_cost_per_kg: form.bag_cost_per_kg ? parseFloat(form.bag_cost_per_kg) : undefined,
        warehouse_id: Number(form.warehouse_id),
        notes: form.notes || undefined,
      })
      toast.success('Lote de empaquetado solicitado')
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear el lote de empaquetado')
    } finally {
      setSaving(false)
    }
  }

  const openEdit = (batch: PackagingBatch) => {
    setEditingBatch(batch)
    setEditForm({
      warehouse_id: String(batch.warehouse_id),
      notes: batch.notes || '',
      origin_warehouse_id: '',
      transit_eta: '',
      status: batch.status === 'in_transit' ? 'in_transit' : 'at_rest',
      product_id: String(batch.product_id),
      kg_input: String(batch.kg_input),
    })
    setEditError('')
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingBatch) return
    const isPending = editingBatch.status === 'pending'
    if (isPending) {
      const kg = parseFloat(editForm.kg_input)
      if (!kg || kg <= 0) {
        setEditError('Ingresa un peso de entrada válido')
        return
      }
    }
    setEditSaving(true)
    setEditError('')
    try {
      const payload: any = { warehouse_id: Number(editForm.warehouse_id), notes: editForm.notes || null }
      if (isPending) {
        payload.product_id = Number(editForm.product_id)
        payload.kg_input = parseFloat(editForm.kg_input)
      } else {
        payload.origin_warehouse_id = editForm.origin_warehouse_id ? Number(editForm.origin_warehouse_id) : null
        payload.transit_eta = editForm.transit_eta || null
        payload.status = editForm.status
      }
      await packagingBatchesApi.update(editingBatch.id, payload)
      toast.success('Lote de empaquetado actualizado')
      setEditingBatch(null)
      fetchAll()
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar el lote')
    } finally {
      setEditSaving(false)
    }
  }

  const handleComplete = async (batch: PackagingBatch) => {
    if (!confirm(`¿Completar el empaquetado ${batch.batch_code}? Esto sumará el stock al producto "${batch.product_name}".`)) return
    setCompletingId(batch.id)
    try {
      await packagingBatchesApi.complete(batch.id, {})
      toast.success('Empaquetado completado — stock del producto actualizado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al completar el empaquetado')
    } finally {
      setCompletingId(null)
    }
  }

  const handleCancel = async (batch: PackagingBatch) => {
    if (!confirm(`¿Anular el lote de empaquetado ${batch.batch_code}? Esta acción restaurará los kilos reservados en la etapa anterior.`)) return
    setCancelingId(batch.id)
    try {
      await packagingBatchesApi.cancel(batch.id)
      toast.success('Lote de empaquetado anulado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al anular el lote')
    } finally {
      setCancelingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="packaging-status-filter" className="text-xs font-medium text-espresso-500">
            Estado
          </label>
          <select
            id="packaging-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="in_transit">En Tránsito</option>
            <option value="at_rest">En Almacén</option>
            <option value="consumed">Consumido</option>
          </select>
        </div>
        <button
          onClick={openNew}
          disabled={blendingBatches.length === 0 || activeWarehouses.length === 0 || activeProducts.length === 0}
          title={
            blendingBatches.length === 0
              ? 'No hay lotes de mezclado en almacén disponibles para empaquetar'
              : activeProducts.length === 0
              ? 'Crea primero un producto en el catálogo'
              : activeWarehouses.length === 0
              ? 'Crea primero un almacén activo'
              : undefined
          }
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Empaquetado
        </button>
      </div>

      {blendingBatches.length === 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          No hay lotes de mezclado en almacén con kg disponibles. Completa un mezclado primero.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && filteredBatches.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">
            {batches.length === 0 ? 'No hay lotes de empaquetado registrados aún.' : 'No hay lotes con este estado.'}
          </p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Lote" sortKey="batch_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Fecha" sortKey="requested_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Origen" sortKey="source_batch_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Producto" sortKey="product_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Entrada" sortKey="kg_input" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Rest." sortKey="kg_remaining" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo Unit./kg" sortKey="unit_cost_per_kg" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedBatches.map((b) => (
                  <tr key={b.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{b.batch_code}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.requested_date ? new Date(b.requested_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{b.source_batch_code}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{b.product_name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(b.kg_input).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.kg_remaining !== null ? Number(b.kg_remaining).toFixed(2) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.unit_cost_per_kg !== null ? `$${Number(b.unit_cost_per_kg).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap"><StatusBadge status={b.status} isActive={b.is_active} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        {b.status !== 'consumed' && (
                          <TableActionButton onClick={() => openEdit(b)} variant="primary">
                            Editar
                          </TableActionButton>
                        )}
                        {b.status === 'pending' && (
                          <TableActionButton onClick={() => handleComplete(b)} variant="success" disabled={completingId === b.id}>
                            {completingId === b.id ? 'Completando...' : 'Completar'}
                          </TableActionButton>
                        )}
                        {b.is_active !== false && (
                          <TableActionButton
                            onClick={() => handleCancel(b)}
                            variant="danger"
                            disabled={cancelingId === b.id || b.status !== 'pending'}
                            title={
                              b.status !== 'pending'
                                ? 'No se puede anular: una vez completado, el aporte al stock ya no es rastreable individualmente'
                                : undefined
                            }
                          >
                            {cancelingId === b.id ? 'Anulando...' : 'Anular'}
                          </TableActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Nuevo Lote de Empaquetado</h2>
              <p className="text-sm text-espresso-500 mt-1">
                Al completarse, el kg empaquetado se suma automáticamente al stock del producto elegido.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="pack-source" className="block text-sm font-medium text-espresso-700 mb-1">
                  Lote de mezclado de origen *
                </label>
                <select
                  id="pack-source"
                  name="source_blending_batch_id"
                  value={form.source_blending_batch_id}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="">Selecciona un lote</option>
                  {blendingBatches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.batch_code} · {Number(b.kg_remaining).toFixed(2)} kg disponibles
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="pack-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                  Kg de entrada *
                </label>
                <input
                  id="pack-kg"
                  type="number"
                  name="kg_input"
                  value={form.kg_input}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="pack-product" className="block text-sm font-medium text-espresso-700 mb-1">
                  Producto *
                </label>
                <select
                  id="pack-product"
                  name="product_id"
                  value={form.product_id}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="">Selecciona un producto</option>
                  {productGroups.map(([maestro, items]) => (
                    <optgroup key={maestro} label={maestro}>
                      {items.map((p) => (
                        <option key={p.id} value={p.id} disabled={!p.is_active}>
                          {p.name}{!p.is_active ? ' (inactivo)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pack-label-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                    Costo/kg etiqueta
                  </label>
                  <input
                    id="pack-label-cost"
                    type="number"
                    name="label_cost_per_kg"
                    value={form.label_cost_per_kg}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="Usa costo actual del insumo"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <div>
                  <label htmlFor="pack-bag-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                    Costo/kg bolsa
                  </label>
                  <input
                    id="pack-bag-cost"
                    type="number"
                    name="bag_cost_per_kg"
                    value={form.bag_cost_per_kg}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    placeholder="Usa costo actual del insumo"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="pack-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén *
                </label>
                <select
                  id="pack-warehouse"
                  name="warehouse_id"
                  value={form.warehouse_id}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="">Selecciona un almacén</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id} disabled={!w.is_active}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="pack-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="pack-notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Editar Lote {editingBatch.batch_code}</h2>
            </div>
            <form onSubmit={handleEditSubmit} className="px-6 py-4 space-y-4">
              {editError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {editError}
                </div>
              )}
              <div>
                <label htmlFor="edit-pack-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén
                </label>
                <select
                  id="edit-pack-warehouse"
                  value={editForm.warehouse_id}
                  onChange={(e) => setEditForm({ ...editForm, warehouse_id: e.target.value })}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              {editingBatch.status === 'pending' && (
                <>
                  <div>
                    <label htmlFor="edit-pack-product" className="block text-sm font-medium text-espresso-700 mb-1">
                      Producto
                    </label>
                    <select
                      id="edit-pack-product"
                      value={editForm.product_id}
                      onChange={(e) => setEditForm({ ...editForm, product_id: e.target.value })}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    >
                      {productGroups.map(([maestro, items]) => (
                        <optgroup key={maestro} label={maestro}>
                          {items.map((p) => (
                            <option key={p.id} value={p.id} disabled={!p.is_active && String(p.id) !== editForm.product_id}>
                              {p.name}{!p.is_active ? ' (inactivo)' : ''}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="edit-pack-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                      Kg de entrada
                    </label>
                    <input
                      id="edit-pack-kg"
                      type="number"
                      value={editForm.kg_input}
                      onChange={(e) => setEditForm({ ...editForm, kg_input: e.target.value })}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    />
                    <p className="mt-1 text-xs text-espresso-500">
                      Si cambias este valor, se ajusta la reserva de kg sobre el lote de mezclado de origen.
                    </p>
                  </div>
                </>
              )}
              {editingBatch.status !== 'pending' && (
                <>
                  <div>
                    <label htmlFor="edit-pack-status" className="block text-sm font-medium text-espresso-700 mb-1">
                      Estado
                    </label>
                    <select
                      id="edit-pack-status"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'at_rest' | 'in_transit' })}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="at_rest">En Almacén</option>
                      <option value="in_transit">En Tránsito</option>
                    </select>
                  </div>
                  {editForm.status === 'in_transit' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="edit-pack-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                          Almacén origen
                        </label>
                        <select
                          id="edit-pack-origin"
                          value={editForm.origin_warehouse_id}
                          onChange={(e) => setEditForm({ ...editForm, origin_warehouse_id: e.target.value })}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="edit-pack-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                          ETA
                        </label>
                        <input
                          id="edit-pack-eta"
                          type="date"
                          value={editForm.transit_eta}
                          onChange={(e) => setEditForm({ ...editForm, transit_eta: e.target.value })}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <label htmlFor="edit-pack-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="edit-pack-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setEditingBatch(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {editSaving ? 'Guardando...' : 'Actualizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
